import React from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "./style/pre-reg-confirmation.scss";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import type {
  OrgConfig,
  VisitPayload,
  OnSiteVisitor,
} from "../../services/api/touchlessService";
import { setCookie } from "../../utils/helpers";
import { BackButton } from "../../components/ui/Button/backButton";
import { useStrings } from "../../hooks/useStrings";

interface VisitorInfo extends VisitPayload {
  id: string;
  fullName: string;
  email: string;
  hostName: string;
  scheduleCheckinDate: string;
  visitPhotoURI?: string;
}

const PreRegConfirmation: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  //   const dispatch = useDispatch<AppDispatch>();

  const { config, themeColors } = useAppConfig();
  const orgConfig = config as OrgConfig;

  const {
    preRegisterData,
    updatePreregisterId,
    updatePreRegisterData,
    updateVisitorTypeId,
    onSiteVisitors,
    updateOnSiteVisitors,
  } = useVisitorData();

  const visitorInfo = preRegisterData as VisitorInfo;
  const topOnSiteMatch: OnSiteVisitor | null = onSiteVisitors?.[0] ?? null;

  const onNotMe = () => {
    updatePreRegisterData(null);
    updateOnSiteVisitors(null);
    navigate("/visitortypes");
  };

  const onBack = () => {
    navigate("/full-name");
  };

  const confirmPreRegister = () => {
    if (visitorInfo) {
      updatePreregisterId(visitorInfo?.id);
      updateVisitorTypeId(visitorInfo?.visitorTypeId ?? null);
      updateOnSiteVisitors(null);
      navigate("/visitorform");
    }
  };

  const handleSignOut = (visitor: OnSiteVisitor) => {
    setCookie("visitId",       visitor.id,                                                    3);
    setCookie("visitorName",   visitor.fullName,                                               3);
    setCookie("orgName",       visitor.orgName  ?? orgConfig?.orgName  ?? "",                  3);
    setCookie("siteName",      visitor.siteName ?? orgConfig?.siteName ?? "",                  3);
    setCookie("signInDateTime",
      visitor.checkinTime ? moment(visitor.checkinTime).format("DD MMM YY hh:mm A") : "",     3);
    if (visitor.visitorTypeId) setCookie("visitorTypeId", visitor.visitorTypeId, 3);
    if (visitor.hostName)      setCookie("hostName",      visitor.hostName,      3);
    navigate("/signout", { replace: true });
  };

  if (!orgConfig) {
    return <div>{S.COMMON.LOADING}</div>;
  }

  // Guard: if we land here with no data at all, go back to full-name
  if (!visitorInfo && !topOnSiteMatch) {
    navigate("/full-name", { replace: true });
    return null;
  }

  return (
    <>
      <div className="main-screen" data-testid="PRC-div-main-screen">
        <div
          className="screen-outer regconfirm-screen"
          data-testid="PRC-div-screen-outer"
        >
          {/* Header */}
          <div
            className="screen-header-fixed"
            data-testid="PRC-div-screen-header-fixed"
          >
            <div
              className="header-fixed-left"
              data-testid="PRC-div-header-fixed-left"
            >
              <BackButton onBack={onBack} />
            </div>
            <div
              className="header-fixed-right"
              data-testid="PRC-div-header-fixed-right"
            ></div>
          </div>

          {/* Main Content */}
          <div className="screen-inner" data-testid="PRC-div-screen-inner">
            <div
              className="screen-subinner"
              data-testid="PRC-div-screen-subinner"
            >
              <div className="screen-body" data-testid="PRC-div-screen-body">

                {/* ── Pre-reg card ── */}
                {visitorInfo && (
                  <>
                    <div className="regconfirm-top" data-testid="PRC-div-regconfirm-top">
                      <h3 data-testid="PRC-h2-confirm-details">{S.PRE_REG_CONFIRMATION.TITLE}</h3>
                      <p data-testid="PRC-p-confirm-details">{S.PRE_REG_CONFIRMATION.SUBTITLE}</p>
                    </div>
                    <div className="rccard rccard--prereg" data-testid="PRC-section-prereg">
                      <div className="rccard-badge rccard-badge--prereg">
                        <i className="bi bi-envelope-check-fill" /> Invited
                      </div>
                      <div className="rccard-body">
                        <div className="rccard-avatar rccard-avatar--prereg">
                          {visitorInfo.fullName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                        </div>
                        <div className="rccard-info">
                          <h4 className="rccard-name">{visitorInfo.fullName}</h4>
                          {visitorInfo.visitorType && (
                            <div className="rccard-row">
                              <i className="bi bi-person-badge" />
                              <span>{visitorInfo.visitorType}</span>
                            </div>
                          )}
                          {visitorInfo.hostName && (
                            <div className="rccard-row">
                              <i className="bi bi-person" />
                              <span>Meeting with: <strong>{visitorInfo.hostName}</strong></span>
                            </div>
                          )}
                          {visitorInfo.scheduleCheckinDate && (
                            <div className="rccard-row">
                              <i className="bi bi-calendar3" />
                              <span>Visiting: {moment(visitorInfo.scheduleCheckinDate).format("D MMM [at] h:mm A")}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-shadow btn-width-full rccard-cta"
                      style={{ backgroundColor: themeColors.btnColor }}
                      onClick={confirmPreRegister}
                      data-testid="PRC-button-thats-me"
                    >
                      {S.COMMON.CONTINUE} &nbsp;<i className="bi bi-arrow-right-short" />
                    </button>
                  </>
                )}

                {/* ── OR divider (only when both) ── */}
                {visitorInfo && topOnSiteMatch && (
                  <div className="regconfirm-divider"><span>or</span></div>
                )}

                {/* ── On-site card ── */}
                {topOnSiteMatch && (
                  <>
                    <div className="regconfirm-top">
                      <h3>{S.PRE_REG_CONFIRMATION.ONSITE_TITLE}</h3>
                      <p>{S.PRE_REG_CONFIRMATION.ONSITE_SUBTITLE}</p>
                    </div>
                    <div className="rccard rccard--onsite" data-testid="PRC-section-onsite">
                      <div className="rccard-badge rccard-badge--onsite">
                        <i className="bi bi-check-circle-fill" /> {S.SIGN_OUT?.STATUS_CHECKED_IN ?? "Checked In"}
                      </div>
                      <div className="rccard-body">
                        <div
                          className="rccard-avatar"
                          style={{
                            backgroundColor: `${themeColors.btnColor}22`,
                            color: themeColors.btnColor,
                          }}
                        >
                          {topOnSiteMatch.fullName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                        </div>
                        <div className="rccard-info">
                          <h4 className="rccard-name">{topOnSiteMatch.fullName}</h4>
                          {topOnSiteMatch.visitorType && (
                            <div className="rccard-row">
                              <i className="bi bi-person-badge" />
                              <span>{topOnSiteMatch.visitorType}</span>
                            </div>
                          )}
                          {topOnSiteMatch.hostName && (
                            <div className="rccard-row">
                              <i className="bi bi-person" />
                              <span>{S.SIGN_OUT?.DETAIL_LABEL_HOST ?? "Host"}: <strong>{topOnSiteMatch.hostName}</strong></span>
                            </div>
                          )}
                          {topOnSiteMatch.checkinTime && (
                            <div className="rccard-row">
                              <i className="bi bi-clock" />
                              <span>{S.PRE_REG_CONFIRMATION.CHECKIN_AT_LABEL} <strong>{moment(topOnSiteMatch.checkinTime).format("h:mm A")}</strong></span>
                            </div>
                          )}
                          {topOnSiteMatch.companyName && (
                            <div className="rccard-row">
                              <i className="bi bi-building" />
                              <span>{topOnSiteMatch.companyName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-shadow btn-width-full rccard-cta"
                      style={{ backgroundColor: themeColors.btnColor }}
                      onClick={() => handleSignOut(topOnSiteMatch)}
                      data-testid="PRC-onsite-signout-btn"
                    >
                      {S.SIGN_OUT?.BTN_SIGN_OUT ?? "Sign Out"} &nbsp;<i className="bi bi-arrow-right-short" />
                    </button>
                  </>
                )}

                {/* ── Not You? link ── */}
                <div className="rccard-notme">
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={onNotMe}
                    data-testid="PRC-button-not-you"
                  >
                    {S.PRE_REG_CONFIRMATION.BTN_NOT_YOU}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreRegConfirmation;
